import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'
import {
  buildScheduleClassOptions,
  scheduleApi,
  sortStagedCourses
} from '@/api/schedule'
import {
  buildBellScheduleDisplayTimeline,
  countSchedulableRows,
  filterScheduleDetailTimelineRows,
  type BellScheduleDisplayRow
} from '@/views/next/schedule/utils/bellScheduleDisplayTimeline'
import type {
  BellSchedule,
  ConflictInfo,
  ScheduleClassOption,
  ScheduleOccurrenceDraft,
  ScheduleRunDetail,
  RelaxationSession,
  ScheduleSnapshotDetail,
  ScheduleSnapshotSummary,
  ScheduleTask,
  ScheduleTaskDetail,
  ScheduleTeacherOption,
  TeacherSchedule
} from '@/types'

import { useScheduleDraft } from '@/composables/useScheduleDraft'
import { useScheduleSnapshots } from '@/composables/useScheduleSnapshots'
import { useSchedulePublishLifecycle } from './schedule/useSchedulePublishLifecycle'
import { useScheduleRunActions } from './schedule/useScheduleRunActions'
import { useScheduleTaskCrud } from './schedule/useScheduleTaskCrud'

/** 排课 Store：状态 + 对外入口；实现委托给 composables / schedule/*，API 不变。 */
export const useScheduleStore = defineStore('schedule', () => {
  const tasks = ref<ScheduleTask[]>([])
  const currentTask = ref<ScheduleTaskDetail | null>(null)
  const latestRun = ref<ScheduleRunDetail | null>(null)
  const latestRelaxationSession = ref<RelaxationSession | null>(null)

  const snapshots = ref<ScheduleSnapshotSummary[]>([])
  const activeSnapshot = ref<ScheduleSnapshotDetail | null>(null)

  const draftOccurrences = ref<ScheduleOccurrenceDraft[]>([])
  const baselineDraftOccurrences = ref<ScheduleOccurrenceDraft[]>([])

  const selectedClassId = ref<number | null>(null)
  const selectedSubgroupId = ref<number | null>(null)
  const selectedTeacherId = ref<number | null>(null)
  const teacherScheduleCache = ref<Record<number, TeacherSchedule | null>>({})

  const currentView = ref<'tasks' | 'detail'>('tasks')
  const pendingAutoOpenTaskId = ref<number | null>(null)

  const loading = ref(false)
  const detailLoading = ref(false)
  const saving = ref(false)
  const running = ref(false)

  const error = ref<Error | null>(null)
  const detailError = ref<Error | null>(null)

  /** 学期当前关联的作息方案（调课网格与「作息时间」模块对齐，可随方案更新而刷新） */
  const linkedBellSchedule = ref<BellSchedule | null>(null)

  // ==================== 初始化 Composables ====================

  const draftComposable = useScheduleDraft()
  const snapshotsComposable = useScheduleSnapshots()

  // ==================== Computed 属性 ====================

  const classOptions = computed<ScheduleClassOption[]>(() =>
    currentTask.value ? buildScheduleClassOptions(currentTask.value) : []
  )

  const teacherOptions = computed<ScheduleTeacherOption[]>(() =>
    draftComposable.mergeTeacherOptions(currentTask.value, activeSnapshot.value, draftOccurrences.value)
  )

  const days = computed(() => currentTask.value?.workingDays ?? [])

  const displayTimelineRows = computed<BellScheduleDisplayRow[]>(() => {
    if (!linkedBellSchedule.value) {
      return []
    }
    return filterScheduleDetailTimelineRows(buildBellScheduleDisplayTimeline(linkedBellSchedule.value))
  })

  /** 作息方案更新后节次数量与任务落库不一致时提示用户同步任务 */
  const bellScheduleVsTaskPeriodMismatch = computed(() => {
    if (!linkedBellSchedule.value || !currentTask.value?.periods?.length) {
      return false
    }
    const n = countSchedulableRows(buildBellScheduleDisplayTimeline(linkedBellSchedule.value))
    return n !== currentTask.value.periods.length
  })

  /** 兼容旧逻辑：可排课节次列表（与 displayTimelineRows 中有 enginePeriod 的行一致） */
  const periods = computed(() => currentTask.value?.periods ?? [])

  const hasUnsavedChanges = computed(
    () =>
      draftComposable.serializeDraftOccurrences(draftOccurrences.value) !==
      draftComposable.serializeDraftOccurrences(baselineDraftOccurrences.value)
  )

  const classSchedule = computed(() => {
    if (!selectedClassId.value) {
      return []
    }
    return draftComposable.buildClassSchedule(
      draftOccurrences.value,
      selectedClassId.value,
      selectedSubgroupId.value
    )
  })

  const stagedCourses = computed(() => {
    const originMap = new Map<string, { originalDay: number | null; originalPeriod: number | null }>()
    for (const staged of activeSnapshot.value?.stagedCourses ?? []) {
      const key = staged.occurrenceKey
        ?? (staged.taskActivityId != null && staged.occurrenceIndex != null
          ? `${staged.taskActivityId}:${staged.occurrenceIndex}`
          : null)
      if (!key) continue
      originMap.set(key, {
        originalDay: staged.originalDay ?? null,
        originalPeriod: staged.originalPeriod ?? null
      })
    }
    return sortStagedCourses(
      draftComposable.buildStagedCourses(draftOccurrences.value),
      classOptions.value
    ).map((course) => {
      const origin = originMap.get(course.occurrenceKey ?? course.id)
      if (!origin) return course
      return {
        ...course,
        originalDay: origin.originalDay ?? course.originalDay,
        originalPeriod: origin.originalPeriod ?? course.originalPeriod
      }
    })
  })

  const selectedTeacher = computed(() => {
    if (!selectedTeacherId.value) {
      return null
    }

    const option = teacherOptions.value.find((item) => item.id === selectedTeacherId.value) ?? null
    const base = draftComposable.buildTeacherScheduleFromDrafts(draftOccurrences.value, option)
    if (!base) return base

    // 外部占用始终可见：从教师完整课表缓存中取出其他已发布任务的课位并入当前面板
    const externalSlots = (teacherScheduleCache.value[selectedTeacherId.value]?.schedule ?? [])
      .filter((slot) => slot.external === true)
    if (externalSlots.length === 0) return base
    return { ...base, schedule: [...base.schedule, ...externalSlots] }
  })

  const selectedTeacherStats = computed(() => {
    if (!selectedTeacher.value) {
      return null
    }

    const weeklyHours = selectedTeacher.value.schedule.length
    const classCount = new Set(selectedTeacher.value.schedule.map((slot) => slot.className)).size
    const slotCount = countSchedulableRows(displayTimelineRows.value)
    const freeSlots = Math.max(days.value.length * slotCount - weeklyHours, 0)

    return {
      weeklyHours,
      classCount,
      freeSlots
    }
  })

  // ==================== 辅助函数 ====================

  function ensureSelections(): void {
    const selectedClassOption = classOptions.value.find(
      (item) =>
        item.id === selectedClassId.value &&
        (item.subgroupId ?? null) === (selectedSubgroupId.value ?? null)
    )
    if (!selectedClassOption) {
      const fallbackOption =
        classOptions.value.find((item) => item.id === selectedClassId.value) ??
        classOptions.value[0]
      selectedClassId.value = fallbackOption?.id ?? null
      selectedSubgroupId.value = fallbackOption?.subgroupId ?? null
    }

    if (!teacherOptions.value.some((item) => item.id === selectedTeacherId.value)) {
      selectedTeacherId.value = teacherOptions.value[0]?.id ?? null
    }
    // 首屏默认教师也要拉取完整课表（外部占用始终可见），缓存命中时不会重复请求
    if (selectedTeacherId.value != null) {
      void selectTeacher(selectedTeacherId.value)
    }
  }

  const {
    clearPendingAutoOpen,
    fetchTasks,
    fetchLinkedBellSchedule,
    backToTasks,
    refreshCurrentTask,
    selectTask,
    tryAutoOpenCompletedTask,
    createTask,
    updateTask,
    deleteTask,
    copyTask,
    refreshRelaxationSession,
    setLatestRelaxationSession,
    acknowledgeTaskOutcome
  } = useScheduleTaskCrud({
    tasks,
    currentTask,
    latestRun,
    latestRelaxationSession,
    snapshots,
    activeSnapshot,
    draftOccurrences,
    baselineDraftOccurrences,
    selectedClassId,
    selectedSubgroupId,
    selectedTeacherId,
    teacherScheduleCache,
    currentView,
    pendingAutoOpenTaskId,
    loading,
    detailLoading,
    saving,
    error,
    detailError,
    linkedBellSchedule,
    draftComposable,
    ensureSelections
  })

  const {
    validateForPublish,
    publishTask,
    unpublishTask,
    validateForActivate,
    activateTask,
    deactivateTask
  } = useSchedulePublishLifecycle({
    currentTask,
    tasks,
    snapshots,
    activeSnapshot
  })

  const {
    startRun,
    cancelTaskScheduling,
    requestLatestRunSnapshot
  } = useScheduleRunActions({
    currentTask,
    latestRun,
    running,
    refreshCurrentTask,
    fetchTasks
  })

  /**
   * 选择班级
   */
  function selectClass(classId: number | null, subgroupId: number | null = null): void {
    if (classId == null) {
      selectedClassId.value = null
      selectedSubgroupId.value = null
      return
    }

    const normalizedSubgroupId = subgroupId ?? null
    const option =
      classOptions.value.find(
        (item) =>
          item.id === classId &&
          (item.subgroupId ?? null) === normalizedSubgroupId
      ) ??
      classOptions.value.find((item) => item.id === classId)

    selectedClassId.value = option?.id ?? classId
    selectedSubgroupId.value = option?.subgroupId ?? normalizedSubgroupId
  }

  /**
   * 选择教师（带缓存）
   */
  async function selectTeacher(teacherId: number | null): Promise<void> {
    selectedTeacherId.value = teacherId

    if (!teacherId || !currentTask.value || teacherScheduleCache.value[teacherId]) {
      return
    }

    try {
      teacherScheduleCache.value[teacherId] = await scheduleApi.getTeacherSchedule(
        currentTask.value.id,
        teacherId
      )
    } catch {
      teacherScheduleCache.value[teacherId] = null
    }
  }

  /**
   * 清除教师选择
   */
  function clearSelectedTeacher(): void {
    selectedTeacherId.value = null
  }

  /**
   * 查找草稿课时
   */
  function findOccurrence(key: string): ScheduleOccurrenceDraft | null {
    return draftOccurrences.value.find((occurrence) => occurrence.key === key) ?? null
  }

  /**
   * 移动课时到指定时段（带冲突检测）
   */
  function moveOccurrenceToSlot(key: string, dayOfWeek: number, period: number, force = false): ConflictInfo {
    return draftComposable.moveOccurrenceToSlot(key, dayOfWeek, period, force)
  }

  /**
   * 移动课时到待排区
   */
  function moveOccurrenceToStaging(key: string): boolean {
    return draftComposable.moveOccurrenceToStaging(key)
  }

  /**
   * 丢弃未保存的更改
   */
  function discardChanges(): void {
    draftComposable.discardChanges()
  }

  /**
   * 保存快照
   */
  async function saveSnapshot(options?: {
    name?: string
    remark?: string
    skipDiagnose?: boolean
  }): Promise<ScheduleSnapshotDetail | null> {
    return snapshotsComposable.saveSnapshot(options)
  }

  async function saveActiveSnapshot(options?: {
    remark?: string
    skipDiagnose?: boolean
  }): Promise<ScheduleSnapshotDetail | null> {
    return snapshotsComposable.saveActiveSnapshot(options)
  }

  /**
   * 删除快照
   */
  async function deleteSnapshot(snapshotId: number): Promise<void> {
    if (!currentTask.value) {
      return
    }

    await scheduleApi.deleteSnapshot(currentTask.value.id, snapshotId)

    snapshots.value = snapshots.value.filter((item) => item.id !== snapshotId)

    if (activeSnapshot.value?.id === snapshotId) {
      activeSnapshot.value = null
      if (snapshots.value.length > 0) {
        await draftComposable.loadActiveSnapshot(snapshots.value[0].id)
      } else {
        draftOccurrences.value = []
        baselineDraftOccurrences.value = []
      }
    }
  }

  /**
   * 重置排课 Store 全部状态。
   * 用于切换租户/组织、登出等场景，避免上一个租户的排课数据残留导致跨租户数据泄漏。
   */
  function resetScheduleState(): void {
    tasks.value = []
    currentTask.value = null
    latestRun.value = null
    snapshots.value = []
    activeSnapshot.value = null
    draftOccurrences.value = []
    baselineDraftOccurrences.value = []
    selectedClassId.value = null
    selectedSubgroupId.value = null
    selectedTeacherId.value = null
    teacherScheduleCache.value = {}
    currentView.value = 'tasks'
    pendingAutoOpenTaskId.value = null
    loading.value = false
    detailLoading.value = false
    saving.value = false
    running.value = false
    error.value = null
    detailError.value = null
    linkedBellSchedule.value = null
  }

  // ==================== 暴露公共 API ====================

  return {
    // State
    tasks,
    currentTask,
    latestRun,
    latestRelaxationSession,
    snapshots,
    activeSnapshot,
    draftOccurrences,
    baselineDraftOccurrences,

    // Computed
    classOptions,
    teacherOptions,
    selectedClassId,
    selectedSubgroupId,
    selectedTeacherId,
    classSchedule,
    selectedTeacher,
    selectedTeacherStats,
    stagedCourses,
    currentView,
    pendingAutoOpenTaskId,
    days,
    periods,
    displayTimelineRows,
    linkedBellSchedule,
    bellScheduleVsTaskPeriodMismatch,
    hasUnsavedChanges,

    // Loading/Error 状态
    loading,
    detailLoading,
    saving,
    running,
    error,
    detailError,

    teacherScheduleCache,

    // 任务 CRUD Actions
    fetchTasks,
    selectTask,
    backToTasks,
    createTask,
    updateTask,
    deleteTask,
    copyTask,
    refreshCurrentTask,
    refreshRelaxationSession,
    setLatestRelaxationSession,
    acknowledgeTaskOutcome,
    tryAutoOpenCompletedTask,
    clearPendingAutoOpen,
    fetchLinkedBellSchedule,

    // 快照 Actions
    loadActiveSnapshot: draftComposable.loadActiveSnapshot,

    // 选择 Actions
    selectClass,
    selectTeacher,
    clearSelectedTeacher,

    // 草稿编排 Actions
    findOccurrence,
    moveOccurrenceToSlot,
    moveOccurrenceToStaging,
    discardChanges,

    // 诊断与保存 Actions
    saveSnapshot,
    saveActiveSnapshot,
    deleteSnapshot,

    // 运行控制 Actions
    startRun,
    requestLatestRunSnapshot,
    cancelTaskScheduling,

    // 发布与启用 Actions
    validateForPublish,
    publishTask,
    unpublishTask,
    validateForActivate,
    activateTask,
    deactivateTask,

    // 租户切换/登出时重置状态
    resetScheduleState,

    // 内部辅助方法（供 composable 调用）
    ensureSelections
  }
})

registerTenantScopedReset('schedule', () => useScheduleStore().resetScheduleState())
